import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Stack,
  TextField,
  Typography,
  useTheme,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import PublicIcon from "@mui/icons-material/Public";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TenantService from "../services/tenantService";
import { tokens } from "../theme";
import { COUNTRIES } from "../utils/countries";

export default function EditTenantInfo({ changeEditState }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const tenantService = useMemo(() => new TenantService(), []);
  const tenantId =
    typeof window !== "undefined" ? localStorage.getItem("tenantId") : null;
  const token =
    typeof window !== "undefined" ? localStorage.getItem("tokenTenant") : null;

  const [tenantInfo, setTenantInfo] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchTenantInfo = async () => {
      try {
        const data = await tenantService.getTenantInfo(tenantId, token);
        setTenantInfo(data || {});
      } catch (error) {
        console.error("Tenant bilgileri çekilirken hata oluştu", error);
      }
    };

    if (tenantId && token) {
      fetchTenantInfo();
    }
  }, [tenantService, tenantId, token]);

  const formFields = [
    {
      label: "Institution Name",
      name: "name",
      value: tenantInfo.name || "",
      icon: <PersonOutlineIcon />,
      editable: true,
      placeholder: "Institution name",
    },
    {
      label: "Email",
      name: "email",
      value: tenantInfo.email || "",
      icon: <EmailOutlinedIcon />,
      editable: true,
      placeholder: "Institution email",
    },
    {
      label: "Phone Number",
      name: "phone_number",
      value: tenantInfo.phone_number || "",
      icon: <PhoneIphoneOutlinedIcon />,
      editable: true,
      placeholder: "Phone number",
      numeric: true,
    },
    {
      label: "Nationality",
      name: "nationality",
      value: tenantInfo.nationality || "",
      icon: <PublicIcon />,
      editable: true,
      placeholder: "Select Nationality",
      isSelect: true,
    },
    {
      label: "Plan Type",
      name: "plan_type",
      value: tenantInfo.plan_type || "",
      icon: <WorkspacePremiumOutlinedIcon />,
      editable: false,
    },
  ];

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setTenantInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (!tenantId || !token) {
      return;
    }

    setIsSaving(true);
    try {
      const oldNationality = JSON.parse(localStorage.getItem("tenantInfo") || "{}").nationality;
      await tenantService.updateTenant(tenantInfo, tenantId, token);
      
      // Eğer nationality güncellendiyse, localStorage'ı ve dili güncelle
      if (tenantInfo.nationality !== undefined && tenantInfo.nationality !== oldNationality) {
        // localStorage'daki tenantInfo bilgisini güncelle
        localStorage.setItem("tenantInfo", JSON.stringify(tenantInfo));
        
        // Nationality'ye göre dil ayarla (nationality değiştiği için force=true)
        const { setLanguageFromNationality } = await import("../utils/languageFromNationality");
        setLanguageFromNationality(tenantInfo.nationality, true);
      }
      
      changeEditState(false);
    } catch (error) {
      console.error("Güncelleme sırasında bir hata oluştu:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        px: { xs: 1, sm: 2 },
        pb: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
          background: `linear-gradient(135deg, ${colors.primary[500]}dd 0%, ${colors.blueAccent[700]}cc 100%)`,
          px: 3,
          py: 2,
          borderRadius: 3,
          color: colors.grey[100],
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
            Edit Tenant Details
          </Typography>
          <Typography
            variant="body2"
            sx={{ opacity: 0.8, maxWidth: 420 }}
          >
            Update your organization information and keep your contact details
            current.
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2.5}>
        {formFields.map((field) => (
          <Grid item xs={12} key={field.name}>
            <Box
              sx={{
                backgroundColor: "#f9fbff",
                borderRadius: 3,
                border: `1px solid ${colors.primary[200]}55`,
                boxShadow: `0 12px 28px rgba(15, 23, 42, 0.08)`,
                px: 2.5,
                py: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: 2,
                    backgroundColor: `${colors.blueAccent[700]}22`,
                    color: colors.blueAccent[400],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.1rem",
                  }}
                >
                  {field.icon}
                </Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    color: colors.grey[600],
                    fontWeight: 600,
                  }}
                >
                  {field.label}
                </Typography>
              </Stack>

              {field.isSelect ? (
                <FormControl
                  fullWidth
                  disabled={!field.editable}
                  size="medium"
                >
                  <InputLabel>{field.placeholder}</InputLabel>
                  <Select
                    name={field.name}
                    value={field.value}
                    onChange={handleInputChange}
                    label={field.placeholder}
                    IconComponent={ExpandMoreIcon}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: "#ffffff",
                      color: colors.grey[900],
                      "& fieldset": {
                        borderColor: `${colors.primary[200]}44`,
                      },
                      "&:hover fieldset": {
                        borderColor: colors.blueAccent[400],
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: colors.blueAccent[500],
                        boxShadow: `0 0 0 3px ${colors.blueAccent[500]}33`,
                      },
                      "& .MuiSelect-select.Mui-disabled": {
                        WebkitTextFillColor: `${colors.grey[600]} !important`,
                        opacity: 1,
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>{field.placeholder}</em>
                    </MenuItem>
                    {COUNTRIES.map((country) => (
                      <MenuItem key={country.code} value={country.name}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
              <TextField
                name={field.name}
                value={field.value}
                onChange={(event) => {
                  if (field.numeric) {
                    const { value } = event.target;
                    if (!/^\d*$/.test(value)) return;
                  }
                  handleInputChange(event);
                }}
                placeholder={field.placeholder}
                disabled={!field.editable}
                fullWidth
                size="medium"
                InputProps={{
                  sx: {
                    borderRadius: 2,
                    backgroundColor: "#ffffff",
                    color: colors.grey[900],
                    "& .MuiInputBase-input": {
                      color: colors.grey[900],
                    },
                    "& fieldset": {
                      borderColor: `${colors.primary[200]}44`,
                    },
                    "&:hover fieldset": {
                      borderColor: colors.blueAccent[400],
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: colors.blueAccent[500],
                      boxShadow: `0 0 0 3px ${colors.blueAccent[500]}33`,
                    },
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: `${colors.grey[600]} !important`,
                      opacity: 1,
                    },
                  },
                }}
              />
              )}
            </Box>
          </Grid>
        ))}
      </Grid>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        justifyContent="flex-end"
        sx={{ pt: 2 }}
      >
        <Button
          variant="outlined"
          onClick={() => changeEditState(false)}
          sx={{
            textTransform: "none",
            borderRadius: 2.5,
            px: 3,
            borderColor: `${colors.redAccent[500]}55`,
            color: colors.redAccent[400],
            fontWeight: 600,
            "&:hover": {
              borderColor: colors.redAccent[500],
              backgroundColor: `${colors.redAccent[500]}12`,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isSaving}
          onClick={handleSave}
          sx={{
            textTransform: "none",
            borderRadius: 2.5,
            px: 3,
            fontWeight: 700,
            background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[600]} 100%)`,
            boxShadow: `0 12px 28px rgba(37, 99, 235, 0.25)`,
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: `0 18px 34px rgba(37, 99, 235, 0.32)`,
            },
          }}
        >
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </Stack>
    </Box>
  );
}
